/**
 * @file    chartOptionsBuilder.ts
 * @scope   SHARED
 * @summary Pure builders that turn the Guardrails chart-type tree into the flat, sorted option
 *          list rendered by the chart-type dropdown, and that resolve which option must be
 *          preselected for the widget being edited.
 * @consumes Guardrails chart-types payload, the widget catalogue resolver
 * @produces buildChartOptions, resolveInitialChartOption
 * @sideEffects None. Both functions are pure; the caller owns the state updates.
 */
import { ChartSelector, DIYChartOption, IconSelectors } from 'constants/chart-selector';
import { library } from 'components';
import { chartTypeLabel, ChartTypeLabelKey } from '../constants/chartTypeLabel';
import { excludedChartTypes } from '../constants/chartCapabilities';
import { ResolvedChartWidget } from './aliasResolver';

/** Marker used while sorting so DataGrid children stay grouped under their parent. */
const DATA_GRID_PARENT = 'DataGrid';
const DATA_GRID_SORT_PREFIX = 'DataGrid ';

/**
 * A chart option is only offered when the widget catalogue can actually render it.
 * The resolver is injected so this module never reaches into the global store.
 */
export type WidgetResolver = (type: DIYChartOption) => ResolvedChartWidget | undefined;

/**
 * Child charts are identified by their first business rule; charts without rules fall back to
 * their own chart type.
 */
const getFirstRule = (chartType: string, rules: string[] = []): string => {
  const [firstRule] = rules;
  return firstRule ? firstRule : chartType;
};

/**
 * Flattens the Guardrails chart-type tree into dropdown options.
 *
 * The API returns a two-level tree (a chart type may expose rule-driven children, as DataGrid
 * does). The dropdown renders a flat list, so children are appended to the same array while
 * keeping a `parent` back-reference and an `isChild` marker.
 *
 * @param types          Chart types to flatten (the tree root on the first call).
 * @param resolveWidget  Tells whether the widget catalogue supports a given chart type.
 * @param options        Accumulator shared across recursive calls.
 * @param isChild        `true` while walking a `children` branch.
 * @param parent         Chart type of the branch being walked.
 */
const flattenChartTypes = (
  types: DIYChartOption[],
  resolveWidget: WidgetResolver,
  options: DIYChartOption[],
  isChild = false,
  parent = 'root'
): void => {
  for (const type of types) {
    const icon = type.chartType as ChartSelector;
    const widget = resolveWidget(type);
    if (!widget) continue;

    if (!excludedChartTypes.includes(type.chartType)) {
      options.push({
        ...type,
        label: type?.label ? type.label : chartTypeLabel[type.chartType as ChartTypeLabelKey],
        icon: IconSelectors.icons[icon],
        component: widget.alias,
        value: isChild ? getFirstRule(type.chartType, type.rules) : type.chartType,
        isChild,
        isOpen: false,
        parent,
      });
    }

    if (type.children && type.children?.length > 0) {
      flattenChartTypes(
        type.children as DIYChartOption[],
        resolveWidget,
        options,
        true,
        type.chartType
      );
    }
  }
};

/**
 * Sorts the options alphabetically while keeping DataGrid children next to their parent.
 *
 * WHY: the labels are temporarily prefixed with "DataGrid " so a plain alphabetical sort places
 * the children right after the DataGrid entry; the prefix is stripped again afterwards. This
 * preserves the exact ordering the original drawer produced.
 */
const sortChartOptions = (options: DIYChartOption[]): void => {
  const dataGridIndexes = options.map((option, index) =>
    option.parent === DATA_GRID_PARENT ? index : -1
  );

  dataGridIndexes.forEach((i) => {
    if (i !== -1) options[i].label = DATA_GRID_SORT_PREFIX + options[i].label;
  });

  options.sort((a, b) => {
    const textA = a.label.toUpperCase();
    const textB = b.label.toUpperCase();
    return textA < textB ? -1 : textA > textB ? 1 : 0;
  });

  dataGridIndexes.forEach((i) => {
    if (i !== -1) options[i].label = options[i].label.replace(DATA_GRID_SORT_PREFIX, '');
  });
};

/**
 * Builds the full dropdown option list from a Guardrails chart-types response.
 *
 * WHY: `TextBlock` is appended when missing because it is a client-side-only widget that
 * Guardrails does not know about, yet the drawer must always offer it.
 *
 * @param types         Raw chart-types array from Guardrails (mutated to append TextBlock, as the
 *                      original implementation did).
 * @param resolveWidget Widget catalogue resolver.
 * @returns The flat, sorted option list.
 */
export const buildChartOptions = (types: any, resolveWidget: WidgetResolver): DIYChartOption[] => {
  if (!types.some((t: any) => t.chartType == 'TextBlock')) {
    types.push({ chartType: 'TextBlock', label: 'Text Block', description: '', rules: [] });
  }

  const chartOptions: DIYChartOption[] = [];
  flattenChartTypes(types, resolveWidget, chartOptions);
  sortChartOptions(chartOptions);
  return chartOptions;
};

/** Outcome of resolving which option the dropdown must start on. */
export type InitialChartOptionResult = {
  /** Option to preselect, `undefined` when nothing matches. */
  option: DIYChartOption | undefined;
  /**
   * `true` when the resolution should also publish `selectedDiyChildChart` to the store.
   * WHY: brand-new and TextBlock widgets deliberately skip that publication in the original code.
   */
  shouldPublishChildChart: boolean;
};

/** Inputs required to resolve the initial chart option. */
export type ResolveInitialChartOptionParams = {
  /** Options produced by {@link buildChartOptions}. */
  options: DIYChartOption[];
  /** Widget currently being edited. */
  customWidget: any;
  /** ENTERPRISE-ONLY: `true` when the widget is an Enterprise V2 configuration. */
  isDiyWidgetEngine: boolean;
  /**
   * ENTERPRISE-ONLY: dropdown option value of the Enterprise chart type to preselect.
   * WHY injected: the Enterprise chart-type registry lives under `DiyEnterprise` and this module
   * is in `common/`, which must not depend on it.
   */
  enterpriseOptionValue?: string;
};

/**
 * Decides which chart option the dropdown starts on, in priority order:
 *
 * 1. Brand-new widget (Custom/Query) or Medusa-backed → first available option.
 * 2. TextBlock → the TextBlock option.
 * 3. ENTERPRISE-ONLY: Enterprise → the option of the resolved Enterprise chart type.
 * 4. Saved child chart → match by chart type **and** business rule.
 * 5. Saved regular chart → match by chart type.
 */
export const resolveInitialChartOption = ({
  options,
  customWidget,
  isDiyWidgetEngine,
  enterpriseOptionValue,
}: ResolveInitialChartOptionParams): InitialChartOptionResult => {
  if (
    customWidget.alias === library.CUSTOM_WIDGET ||
    customWidget.alias === library.QUERY_WIDGET ||
    customWidget.hasMedusaQuery
  ) {
    return { option: options[0], shouldPublishChildChart: false };
  }

  if (customWidget.alias === 'TextBlock') {
    return {
      option: options.find((o) => o.chartType === customWidget.alias),
      shouldPublishChildChart: false,
    };
  }

  // ENTERPRISE-ONLY: enterprise widgets map to the option of their registered chart type.
  if (isDiyWidgetEngine) {
    return {
      option: options.find((o) => o.value === enterpriseOptionValue),
      shouldPublishChildChart: true,
    };
  }

  const [rule] = customWidget?.customWidgetConfig?.rules || [];
  const savedChart = customWidget.customWidgetConfig?.chart;

  // Child charts share a chart type, so the business rule disambiguates which one was saved.
  const option =
    customWidget.customWidgetConfig?.metadata?.isChild && rule
      ? options.find(
          (o) => o.chartType === savedChart && o.rules?.includes(rule.ruleName) && o.isChild
        )
      : options.find((o) => o.chartType === savedChart);

  return { option, shouldPublishChildChart: true };
};
